// Pure grouping/sorting for the dashboard project rail. No React, no api —
// extracted from Dashboard.js so it can be unit-tested (the page itself pulls
// heavy children, and the repo convention is to test pure functions instead).
//
// The old `sortProjects` closed over the page's `sort` state, which is why it
// had no test; here `sort` is an argument.

// Mirrors FileRepository's pattern. Consumed by the rail's sort <select>.
export const SORT_OPTIONS = [
  { value: 'updatedAt-desc', label: 'Newest first' },
  { value: 'updatedAt-asc', label: 'Oldest first' },
  { value: 'name-asc', label: 'Name A→Z' },
  { value: 'name-desc', label: 'Name Z→A' },
];

export const DEFAULT_SORT = 'updatedAt-desc';

/** Copy of `list`, ordered by a `field-dir` key from SORT_OPTIONS. */
export function sortProjects(list, sort = DEFAULT_SORT) {
  const [field, dir] = String(sort).split('-');
  const mult = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    if (field === 'name') {
      return mult * (a.name || '').localeCompare(b.name || '');
    }
    // ISO-8601 UTC throughout, so a string compare IS a chronological compare.
    const av = a.updatedAt || '';
    const bv = b.updatedAt || '';
    if (av === bv) return 0;
    return mult * (av < bv ? -1 : 1);
  });
}

/**
 * A row's identity for React keys and for the open-menu marker.
 *
 * An owned and a shared project can carry the SAME projectId (a shared copy
 * keeps the owner's id), and today's Dashboard compares `menuOpenId` against a
 * bare projectId — so opening the ⋯ menu on one can open it on both. Scoping by
 * owner fixes that.
 */
export function projectKey(project) {
  return `${project.shared ? project.ownerId || 'shared' : 'me'}:${project.projectId}`;
}

/** Is this project waiting on the user's attention (backend-computed)? */
export function isUnseen(project) {
  return !!project?.unseen;
}

/**
 * Partition owned + shared projects into the rail's four fixed sections.
 *
 * A project appears in EXACTLY ONE of starred / needsAttention / recents.
 * Starred wins over needsAttention deliberately: a project you are watching
 * should stay where you put it rather than jumping sections every time a run
 * finishes. Its row still shows the unseen dot, so nothing is hidden.
 *
 * `search` filters every section, including shared — today's Dashboard filters
 * owned projects only, so typing a name silently left the shared list intact.
 */
export function groupProjects(projects = [], shared = [], opts = {}) {
  const { sort = DEFAULT_SORT, search = '' } = opts;
  const needle = search.trim().toLowerCase();
  const match = (p) => !needle || (p.name || '').toLowerCase().includes(needle);

  const owned = (projects || []).filter(match);
  const starred = [];
  const needsAttention = [];
  const recents = [];
  for (const p of owned) {
    if (p.pinned) starred.push(p);
    else if (isUnseen(p)) needsAttention.push(p);
    else recents.push(p);
  }

  return {
    starred: sortProjects(starred, sort),
    needsAttention: sortProjects(needsAttention, sort),
    recents: sortProjects(recents, sort),
    shared: sortProjects((shared || []).filter(match), sort),
  };
}

/**
 * The rail's four sections, in render order — the expanded accordion's fixed
 * vocabulary, and its only reader now that the collapsed strip is the toggle
 * alone (which is why the entries carry no standalone `icon` glyph any more).
 *
 * The `name` keys are exactly `groupProjects`' return keys — pinned by a test,
 * because a typo would render a section with a permanently-zero count.
 */
export const RAIL_SECTIONS = [
  { name: 'starred', title: '★ Starred' },
  { name: 'needsAttention', title: '● Needs attention' },
  { name: 'recents', title: 'Recents' },
  { name: 'shared', title: 'Shared with me' },
];

/** Total rows the rail will render — drives the "no projects yet" empty state. */
export function countProjects(groups) {
  return (
    groups.starred.length +
    groups.needsAttention.length +
    groups.recents.length +
    groups.shared.length
  );
}

// Human-readable reason for a "Needs attention" row, keyed by the backend's
// `lastActivity.kind`. Change-one-change-both with ACTIVITY_KINDS in
// product_backend/app/services/project_activity.py — an unlisted kind falls
// back to a neutral label rather than rendering a raw enum at the user.
export const ACTIVITY_LABELS = {
  step_done: 'Step finished',
  run_done: '✓ Run complete',
  run_failed: '⚠ Run failed',
  awaiting_input: '❓ Needs your input',
  qc_drift: '⚠ Quality check drifted',
  qc_failed: '⚠ Quality check failed',
  qc_incomplete: '⚠ Quality check incomplete',
  qc_passed: '✓ Quality check recovered',
};

export function activityLabel(project) {
  const kind = project?.lastActivity?.kind;
  if (!kind) return null;
  return ACTIVITY_LABELS[kind] || 'Updated';
}

/**
 * Compact "2h" / "3d" stamp for a row. Absolute date past a week — "38d" is
 * not something anyone reads as a date.
 */
export function relativeTime(iso, now = Date.now()) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return 'now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days <= 7) return `${days}d`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
